import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerNav,
  DrawerContent,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import { StatusBadge } from '@/components/ui/status-badge';
import { Spinner } from '@/components/ui/spinner';
import { useDIDStore } from '../stores/did-store';
import { useDID } from '../hooks';
import { formatPhoneNumber } from '@/lib/utils';
import {
  RefreshCcw,
  PhoneForwarded,
  Printer,
  Users,
  UserCheck,
  Shield,
  PhoneOff,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Tab content components
import { FailoverTab } from './tabs/failover-tab';
import { ForwardTab } from './tabs/forward-tab';
import { FaxToEmailTab } from './tabs/fax-to-email-tab';
import { ConferenceTab } from './tabs/conference-tab';
import { CallerIDCNAMTab } from './tabs/caller-id-cnam-tab';
import { E911Tab } from './tabs/e911-tab';

import type { DIDTabId } from '../types';

// Nav items - new tab order with correct icons
interface DrawerNavItem {
  id: DIDTabId;
  icon: LucideIcon;
  label: string;
}

const navItems: DrawerNavItem[] = [
  { id: 'failover', icon: RefreshCcw, label: 'Failover' },
  { id: 'forward', icon: PhoneForwarded, label: 'Call Forwarding' },
  { id: 'fax-to-email', icon: Printer, label: 'eFax' },
  { id: 'conference', icon: Users, label: 'Conference' },
  { id: 'caller-id-cnam', icon: UserCheck, label: 'Caller ID / CNAM' },
  { id: 'e911', icon: Shield, label: 'E911' },
];

export function DIDDetailDrawer() {
  const { selectedDID, isDetailModalOpen, activeTab, closeDetailModal, setActiveTab, openDisconnectModal } =
    useDIDStore();
  const { data: did, isLoading } = useDID(selectedDID);

  if (!selectedDID) return null;

  // Get location string
  const location = did
    ? [did.rate_center, did.state].filter(Boolean).join(', ') || 'Unknown'
    : '';

  const handleDisconnect = () => {
    if (selectedDID) {
      openDisconnectModal(selectedDID);
    }
  };

  return (
    <SlideOverDrawer
      isOpen={isDetailModalOpen}
      onClose={closeDetailModal}
      width="xl"
    >
      {/* Dark Header */}
      <DrawerHeader
        title={formatPhoneNumber(selectedDID)}
        subtitle={`${location} · ID: ${did?.id || '...'}`}
        onClose={closeDetailModal}
        badge={<StatusBadge status="active" />}
      />

      {/* Content with Nav */}
      <DrawerContent>
        {/* Left Nav */}
        <DrawerNav
          items={navItems}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as DIDTabId)}
        />

        {/* Main Content */}
        <DrawerBody className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="animate-fade-in">
              {/* Tab Content */}
              {activeTab === 'failover' && <FailoverTab did={did} />}
              {activeTab === 'forward' && <ForwardTab did={did} />}
              {activeTab === 'fax-to-email' && <FaxToEmailTab did={did} />}
              {activeTab === 'conference' && <ConferenceTab did={did} />}
              {activeTab === 'caller-id-cnam' && <CallerIDCNAMTab did={did} />}
              {activeTab === 'e911' && <E911Tab tn={selectedDID} />}

              {/* Disconnect Button */}
              <div className="mt-8 pt-6 border-t border-slate-200">
                <Button
                  variant="danger"
                  onClick={handleDisconnect}
                >
                  <PhoneOff size={16} className="mr-2" />
                  Disconnect DID
                </Button>
              </div>
            </div>
          )}
        </DrawerBody>
      </DrawerContent>

    </SlideOverDrawer>
  );
}
